import React, {useState, useEffect, useContext, useRef} from 'react';
import { BrowserRouter as Router, Route, Routes, Link } from "react-router-dom";
import Menu from "../include/menu";
import Footer from "../include/footer";
import AOS from "aos";
import "aos/dist/aos.css";
import NewProposalModal from "./new-proposal-modal.js";
import Web3 from "web3";
import { providerURL, truncateAddress, formatDateAndtime, txAfterDone, txDone, txStart} from "../utils";
import Context from "../../../context/context";
import DAO from '../abi/dao.json';
import Staking from '../abi/staking.json';
import Swal  from "sweetalert2";
import { formatUnits } from "ethers";

const ProposalsDao = () => {
  const { account, onConnect, sendRequest, providers, authTokens} = useContext(Context);

  const [loading, setLoading] = useState(true);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const openNewProposal = () => setModalIsOpen(true);
  const closeModal = () => setModalIsOpen(false);

  const [isInSection, setIsInSection] = useState(false);
  useEffect(() => {
    const section = document.querySelector(".ProposalSec");
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInSection(entry.isIntersecting);
      },
      { threshold: 0.5 }
    );

    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const [proposalList, setProposalList] = useState([]);
  const [myProposalList, setMyProposalList] = useState([]);
  const [allProposal, setAllProposal] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");
  const [totalStaked, setTotalStaked] = useState(0);
  const [refresh, setRefresh] = useState(false);

  const filteredProposals = proposalList.filter((proposal) => {
    switch (activeFilter) {
      case "Active":
        return proposal.status === 0;
      case "Succeeded":
        return proposal.status === 2;
      case "Defeated":
        return proposal.status === 1;
      case "Your Proposals":
        return account && proposal.walletAddress.toLowerCase() === account.toLowerCase();
      case "All":
      default:
        return true;
    }
  });

  useEffect(() => {
    AOS.init({
      duration: 1000, 
      once: true,  
    });
    getProposals();
    if(account) 
    {
      getData();
    }
  }, [refresh, account]);

  const getData = async () => {
    const web3 = new Web3(providers);
    const stakingContract = new web3.eth.Contract(Staking.ABI, Staking.Contract);

    let userInfo = await stakingContract.methods.mapUserInfo(account).call();
    setTotalStaked(Number(formatUnits(userInfo.amount, 18)));
  }

  const getProposals = async () => {
    try {
      setLoading(true);
      const web3 = new Web3(providerURL());
      const daoContract = new web3.eth.Contract(DAO.ABI, DAO.Contract);
      const proposalCount = parseInt(await daoContract.methods.proposalCount().call());

      if (proposalCount > 0) {
        const currentTime = Math.floor(Date.now() / 1000);
        const proposalIDs = [];
        const proposalsMap = new Map();

        for (let i = proposalCount; i > 0; i--) {
          const proposalData = await daoContract.methods.proposals(i).call();
          const proposalStatus = parseInt(await daoContract.methods.state(i).call());

          if (currentTime >= (parseInt(proposalData.endTime) + 604800)) {
            break;
          }
          proposalIDs.push(i);

          let myVoteStatus = true;
          if(account) 
          {
            let voteStatus = await daoContract.methods.getReceipt(i, account).call();
                myVoteStatus = voteStatus.hasVoted;
          }

          proposalsMap.set(i, {
            key: i,
            title: '',       
            body: '', 
            walletAddress: proposalData.proposer,
            fundRequest: proposalData.fundRequest,
            forVotes: proposalData.forVotes,
            againstVotes: proposalData.againstVotes,
            abstainVotes: proposalData.abstainVotes,
            claimed: proposalData.claimed,
            voters: proposalData.voters,
            endDate: formatDateAndtime(new Date(parseInt(proposalData.endTime) * 1000)),
            proposer: proposalData.proposer,
            status: proposalStatus,
            completed: '',
            voteStatus: myVoteStatus,
          });
        }

        const response = await sendRequest({ ids: proposalIDs }, 'get-proposal');

        if (response.status === 1 && Array.isArray(response.data)) {
          for (const item of response.data) {
            const id = parseInt(item.id);
            if (proposalsMap.has(id)) {
              const existing = proposalsMap.get(id);
              proposalsMap.set(id, {
                ...existing,
                title: item.title,
                body: item.description,
              });
            }
          }
        }

        const mergedProposals = Array.from(proposalsMap.values());
        setMyProposalList(prev => [...new Set([...prev, ...proposalIDs])]);
        setProposalList(prev => {
          const existingMap = new Map(prev.map(p => [p.key, p]));
          mergedProposals.forEach(p => {
            existingMap.set(p.key, {
              ...existingMap.get(p.key),
              ...p,
            });
          });
          return Array.from(existingMap.values());
        });
      }
    } catch (error) {
      console.error("Error fetching proposals:", error);
    } finally {
      setAllProposal(true);
      setLoading(false);
    }
  };

  const calculateTimeLeft = (targetDate) => {
    const difference = +new Date(targetDate) - +new Date();

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  };

  const CountdownInline = ({ endDate }) => {
    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(endDate));

    useEffect(() => {
      const timer = setInterval(() => {
        setTimeLeft(calculateTimeLeft(endDate));
      }, 1000);

      return () => clearInterval(timer);
    }, [endDate]);

    const { days, hours, minutes, seconds } = timeLeft;

    return (
      <div className="days d-flex align-items-center justify-content-center text-center gap-2">
        <div className="snTime"><span>{days}</span><div>Days</div></div>
        <div className="snTime"><span>{hours}</span><div>Hours</div></div>
        <div className="snTime"><span>{minutes}</span><div>Minutes</div></div>
        <div className="snTime"><span>{seconds}</span><div>Seconds</div></div>
      </div>
    );
  };


  const castVote = async (proposalID, proposalStatus) => {
      const web3 = new Web3(providers);
      const daoContract = new web3.eth.Contract(DAO.ABI, DAO.Contract);
      try {
          await daoContract.methods.castVote(proposalID, proposalStatus).send({
              from: account
          }).on('transactionHash', function(payload) {
              txStart();
          }).on('error', function(error) {
              Swal.fire({
                  title: "Please try again",
                  text: error.message,
                  icon: "error",
                  showCancelButton: false,
                  confirmButtonText: "Ok",
                  showCloseButton: true,
                  customClass: {
                      confirmButton: "btn btn-danger"
                  }
              });
          }).then(async function(receipt) {
              txDone()
              setTimeout(() => {
                txAfterDone()
              }, 2000);
              try {
                  await sendRequest({
                      vote: proposalStatus,
                      proposalID,
                      walletAddress: account
                  }, 'add-vote', authTokens);
              } catch (error) {
                  Swal.fire({
                      title: "Please try again",
                      text: error,
                      icon: "error",
                      showCancelButton: false,
                      confirmButtonText: "Ok",
                      showCloseButton: true,
                      customClass: {
                          confirmButton: "btn btn-danger"
                      }
                  });
              }
              setRefresh(!refresh)
          });
      } catch (error) {
          txDone()
          txAfterDone()
          Swal.fire({
              title: "Please try again",
              text: error.message,
              icon: "error",
              showCancelButton: false,
              confirmButtonText: "Ok",
              showCloseButton: true,
              customClass: {
                  confirmButton: "btn btn-danger"
              }
          });
      }
  }

  const claimFund = async (proposalID) => {
      const web3 = new Web3(providers);
      const daoContract = new web3.eth.Contract(DAO.ABI, DAO.Contract);
      try {
          await daoContract.methods.claimFund(proposalID).send({
              from: account
          }).once('sent', function(payload) {
              txStart();
          }).on('error', function(error) {
              Swal.fire({
                  title: "Please try again",
                  text: error.message,
                  icon: "error",
                  showCancelButton: false,
                  confirmButtonText: "Ok",
                  showCloseButton: true,
                  customClass: {
                      confirmButton: "btn btn-danger"
                  }
              });
          }).then(function(receipt) {
              txDone()
              setTimeout(() => {
                  txAfterDone()
              }, 2000)
              setRefresh(!refresh)
          });
      } catch (error) {
          Swal.fire({
              title: "Please try again",
              text: error.message,
              icon: "error",
              showCancelButton: false,
              confirmButtonText: "Ok",
              showCloseButton: true,
              customClass: {
                  confirmButton: "btn btn-danger"
              }
          });
      }
  }
  
  return (
  <>
  <section className='clippathSec PTGCSEC ProposalSec stakeSec StatsSec leftrightSecSpace padsec'>
      {/*<div className={`makPosBtnCenter ${isInSection ? "active" : ""}`}>
        <button onClick={openNewProposal} className="mb_down1 borderbtn mybtn w-100 d-flex justify-content-between align-items-center">
          <span>Make New Proposal</span> <img className="img-fluid" src="./images/circle_arrow.svg" />
        </button>
      </div>*/}
    <div className="d-flex justify-content-between align-items-center flex-wrap rTabsHere">
      <h2 className="good_times goldFont">Proposals</h2>
      
      <div className="rTabs d-flex gap-1  flex-wrap ">
        {["All", "Active", "Succeeded", "Defeated", "Your Proposals"].map((tab) => (
        <button
          key={tab}
          className={activeFilter === tab ? "active" : ""}
          onClick={() => setActiveFilter(tab)}
        >
          {tab}
        </button>
      ))}
      </div>
    </div>
    <div className="statsALL">
      <div className="row">
        <div className="col-sm-12">
          <div className="subcontent subPad SRTOP p-0" >
            <div className="row HereProposal">
              {loading ? (
                <div className="py-5">
                  <div className="text-center pb-5">
                    <div className="text-center pb-2">
                      <span className="loaderChart"></span>
                    </div>
                    Proposals are loading, please wait...
                  </div>
                </div>
              ) : filteredProposals.length === 0 ? (
                <div className="text-center p-5">
                  <img src="./images/no-data-6.png" className="img-fluid" style={{width: '130px', margin: 'auto'}} />
                  <p>No proposals found – try adjusting your filters or check back later.</p>
                </div>
              ) : (
                filteredProposals.map((item, index) => (
                    <div className="col-sm-6 bordercol">
                      <div className="snstakes"  data-aos-delay="800" data-aos-duration="600">
                        <div className="relative">
                          <h2>{item.title}</h2>
                          <span style={{fontWeight:'300'}}>Notes: {item.body.replace(/<[^>]*>/g, '')}</span>
                          <div className="perVal fInter d-flex align-items-center flex-wrap mt-1">
                            <span><i className="fa-solid fa-user"></i> Creator : <a className="perVal" href={`https://scan.pulsechain.com/address/`+item.walletAddress} target="_blank">{truncateAddress(item.walletAddress)}</a></span>
                            <span className="dot"></span>
                            <span><i className="fa-solid fa-clock f12"></i> End Time : {item.endDate}</span> 
                            <span className="dot"></span>
                            <span><i className="fa-solid fa-clock f12"></i> Total Voters : {item.voters}</span> 
                            <span className="dot"></span>
                            <span><i className="fa-solid fa-thumbs-up"></i> Agree : {parseFloat(parseInt(item.forVotes) / 10**18).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                            <span className="dot"></span>
                            <span><i className="fa-solid fa-thumbs-down"></i> Disagree : {parseFloat(parseInt(item.againstVotes) / 10**18).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                            <span className="dot"></span>
                            <span><i className="fa-solid fa-sack-dollar"></i> Fund Requested : {parseFloat(parseInt(item.fundRequest) / 10**18).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} PLS</span>
                          </div>
                          <div className="mt-4">
                            <div className="d-flex justify-content-between align-items-center flex-wrap colmedia">
                              <div>
                                {
                                   item.status === 0 ?
                                   <>
                                    <div className="perVal fInter align-items-center flex-wrap gap-2">
                                      <span className="d-flex align-items-center gap-1 mb-1">Time Remaining </span>
                                      <div className="days d-flex align-items-center justify-content-center text-center gap-2"><CountdownInline key={item.id} endDate={item.endDate} /></div> 
                                    </div>
                                   </>
                                   :
                                    item.status === 2 ?
                                    <>
                                      <label className="designLabel"><i className="fa-solid fa-circle-check"></i> Success</label>
                                    </>
                                   :
                                    <>
                                      <label className="designLabel"><i className="fa-solid fa-xmark"></i> Defected</label>
                                    </>
                                }
                              </div>
                              <div className="">
                                <div className="someBtns SRTOP  d-flex gap-2">
                                 {
                                  item.status === 0 && account && totalStaked > 0 && !item.voteStatus ? 
                                  <>
                                    <button className="someSnbtn d-flex align-items-center w-auto justify-content-between" onClick={() => castVote(item.key, 1)}>
                                      <span>Agree</span> 
                                      <div>
                                        <div className="iconSR">
                                          <img className="img-fluid" src="./images/ThumbsUp.svg" />
                                        </div>
                                      </div>
                                    </button>
                                    <button className="someSnbtn d-flex align-items-center w-auto justify-content-between" onClick={() => castVote(item.key, 0)}>
                                      <span>Disagree</span> 
                                      <div>
                                        <div className="iconSR">
                                          <img className="img-fluid" src="./images/ThumbsDown.svg" />
                                        </div>
                                      </div>
                                    </button>
                                  </>
                                  :
                                  item.status == 2 && account === item.walletAddress ?
                                   item.claimed ?
                                   <>
                                    <button className="someSnbtn d-flex align-items-center w-auto justify-content-between">
                                      <span>Claim Fund</span> 
                                      <div>
                                        <div className="iconSR">
                                          <img className="img-fluid" src="./images/circlewithdraw.svg" />
                                        </div>
                                      </div>
                                    </button>
                                   </>
                                   :
                                   <>
                                    <button className="someSnbtn d-flex align-items-center w-auto justify-content-between" onClick={() => claimFund(item.key)}>
                                      <span>Claim Fund</span> 
                                      <div>
                                        <div className="iconSR">
                                          <img className="img-fluid" src="./images/circlewithdraw.svg" />
                                        </div>
                                      </div>
                                    </button>
                                   </>
                                  :
                                  <>
                                  <button className="someSnbtn d-flex align-items-center w-auto justify-content-between" disabled>
                                      <span>Agree</span> 
                                      <div>
                                        <div className="iconSR">
                                          <img className="img-fluid" src="./images/ThumbsUp.svg" />
                                        </div>
                                      </div>
                                    </button>
                                    
                                    <button className="someSnbtn d-flex align-items-center w-auto justify-content-between" disabled>
                                      <span>Disagree</span> 
                                      <div>
                                        <div className="iconSR">
                                          <img className="img-fluid" src="./images/ThumbsDown.svg" />
                                        </div>
                                      </div>
                                    </button>
                                  </>
                                 }
                                </div>
                                </div>
                            </div>
                          </div>         
                        </div>
                      </div>
                    </div>
                     ))
                  )}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  <NewProposalModal modalIsOpen={modalIsOpen} closeModal={closeModal} />
  </>
  );
};

export default ProposalsDao;